import React, { useState, useEffect, useContext } from 'react';
import { Badge, Toast, Card, ListGroup } from 'react-bootstrap';
import api from '../utils/api';  // Update path as needed
import { AuthContext } from '../context/AuthContext';

const RecentCourierRequests = () => {
  const { auth } = useContext(AuthContext);
  const [courierRequests, setCourierRequests] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [expandedRequestId, setExpandedRequestId] = useState(null);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');

  useEffect(() => {
    const fetchCourierRequests = async () => {
      if (!auth?.vendorId) {
        console.error('Vendor ID is not defined in AuthContext');
        setIsLoading(false);
        return;
      }
  
      try {
        // We would need a new endpoint to get courier requests by vendor
        const response = await api.get(`/courier-requests/vendor/${auth.vendorId}`);
        setCourierRequests(response.data || []);
      } catch (error) {
        console.error('Error fetching courier requests:', error);
        setCourierRequests([]);
      } finally {
        setIsLoading(false);
      }
    };
  
    fetchCourierRequests();
  }, [auth]);

  const handleRowClick = (requestId) => {
    setExpandedRequestId(expandedRequestId === requestId ? null : requestId);
  };

  const handleStatusChange = async (requestId, status) => {
    try {
        console.log('Attempting to update courier request:', requestId, 'to status:', status);
        
        const response = await api.put(`/courier-requests/${requestId}/status`, { status });
        
        console.log('Server response:', response.data);
        
        if (response.data.success) {
            setCourierRequests(prevRequests =>
                prevRequests.map(request =>
                    request._id === requestId ? response.data.data : request
                )
            );
            
            setToastMessage(`Courier request successfully marked as ${status.toLowerCase()}`);
            setShowToast(true);
        } else {
            throw new Error(response.data.message || 'Failed to update courier request');
        }
    } catch (error) {
        console.error('Error updating courier request status:', error);
        alert(error.response?.data?.message || 'Failed to update courier request status');
    }
  };

  const copyToClipboard = async (text, type) => {
    try {
      await navigator.clipboard.writeText(text);
      setToastMessage(`${type} copied to clipboard!`);
      setShowToast(true);
      setTimeout(() => setShowToast(false), 3000);
    } catch (error) {
      console.error('Failed to copy:', error);
      setToastMessage('Failed to copy to clipboard');
      setShowToast(true);
    }
  };

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 'COMPLETED':
      case 'DELIVERED':
        return 'success';
      case 'CANCELLED':
        return 'danger';
      case 'COURIER_ASSIGNED':
      case 'PICKED_UP':
        return 'primary';
      default:
        return 'warning';
    }
  };

  const toastStyle = {
    position: 'fixed',
    bottom: '1rem',
    right: '1rem',
    zIndex: 1050
  };

  const requestCardStyle = {
    marginBottom: '1rem',
    cursor: 'pointer',
    border: '1px solid #dee2e6',
    borderRadius: '0.375rem',
    transition: 'border-color 0.15s ease-in-out'
  };

  const linkStyle = {
    color: '#0d6efd',
    textDecoration: 'none',
    padding: '4px 8px'
  };

  const copyButtonStyle = {
    border: 'none',
    background: 'none',
    color: '#0d6efd',
    padding: '4px 8px',
    cursor: 'pointer'
  };

  return (
    <div className="bg-white p-4">
      <h2 className="mb-4">Recent Courier Requests</h2>

      <div style={toastStyle}>
        <Toast show={showToast} onClose={() => setShowToast(false)} delay={3000} autohide>
          <Toast.Body className="bg-success text-white">
            {toastMessage}
          </Toast.Body>
        </Toast>
      </div>

      {courierRequests.map((request) => (
        <Card key={request._id} style={requestCardStyle} onClick={() => handleRowClick(request._id)}>
          <Card.Body>
            {/* Request Header */}
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <small className="text-muted">
                  {new Date(request.createdAt).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </small>
                <h6 className="mb-0">
                  Order #{request.orderNumber}
                </h6>
                <div className="mt-1">
                  <Badge bg={getStatusBadgeColor(request.status)} className="me-2">
                    {request.status.replace('_', ' ')}
                  </Badge>
                  {request.pickupTimeOption === 'scheduled' && (
                    <Badge bg="info">
                      Scheduled Pickup
                    </Badge>
                  )}
                </div>
              </div>
              <div className="text-end">
                <h5 className="mb-0">${request.orderTotal?.toFixed(2)}</h5>
                <small className="text-muted">Courier: ${request.courierPayment?.toFixed(2)}</small>
              </div>
            </div>

            {/* Expanded Content */}
            {expandedRequestId === request._id && (
              <div className="mt-3 pt-3 border-top">
                <div className="row">
                  {/* Courier & Customer Information */}
                  <div className="col-md-6 mb-3">
                    <div className="mb-3">
                      <h6 className="mb-2">Courier Information</h6>
                      <Card className="bg-light">
                        <Card.Body>
                          {request.courierId ? (
                            <>
                              <p className="mb-1">{request.courierName || 'Assigned Courier'}</p>
                              <p className="mb-1 text-muted">
                                Assigned: {request.assignedAt ? new Date(request.assignedAt).toLocaleString() : 'Pending'}
                              </p>
                            </>
                          ) : (
                            <p className="mb-1 text-muted">No courier assigned yet</p>
                          )}
                        </Card.Body>
                      </Card>
                    </div>

                    <div className="mb-3">
                      <h6 className="mb-2">Customer Information</h6>
                      <Card className="bg-light">
                        <Card.Body>
                          <p className="mb-1">{request.customerName}</p>
                          <div className="d-flex justify-content-between align-items-center">
                            <span className="text-muted">{request.customerPhone}</span>
                            <a
                              href={`sms:${request.customerPhone}`}
                              style={linkStyle}
                              onClick={(e) => e.stopPropagation()}
                            >
                              Text
                            </a>
                          </div>
                        </Card.Body>
                      </Card>
                    </div>

                    <div className="mb-3">
                      <h6 className="mb-2">Pickup Location</h6>
                      <Card className="bg-light">
                        <Card.Body>
                          <div className="d-flex justify-content-between align-items-start">
                            <div>
                              <p className="mb-1">{request.address}</p>
                              <p className="mb-1 text-muted">Contact: {request.contactPhone}</p>
                              {request.pickupTimeOption === 'scheduled' && request.requestedPickupTime && (
                                <p className="mb-0 text-primary">
                                  Scheduled: {new Date(request.requestedPickupTime).toLocaleString()}
                                </p>
                              )}
                            </div>
                            <button
                              style={copyButtonStyle}
                              onClick={(e) => {
                                e.stopPropagation();
                                copyToClipboard(request.address, 'Address');
                              }}
                            >
                              Copy
                            </button>
                          </div>
                        </Card.Body>
                      </Card>
                    </div>

                    <div className="mb-3">
                      <h6 className="mb-2">Delivery Location</h6>
                      <Card className="bg-light">
                        <Card.Body>
                          <div className="d-flex justify-content-between align-items-start">
                            <div>
                              <p className="mb-0">{request.deliveryAddress}</p>
                            </div>
                            <button
                              style={copyButtonStyle}
                              onClick={(e) => {
                                e.stopPropagation();
                                copyToClipboard(request.deliveryAddress, 'Delivery Address');
                              }}
                            >
                              Copy
                            </button>
                          </div>
                        </Card.Body>
                      </Card>
                    </div>

                    {request.status === 'PENDING' && (
                      <div className="d-flex gap-2">
                        <button
                          className="btn btn-danger flex-grow-1"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleStatusChange(request._id, 'CANCELLED');
                          }}
                        >
                          Cancel Request
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Order Items and Details */}
                  <div className="col-md-6">
                    <h6 className="mb-2">Order Items</h6>
                    <ListGroup variant="flush">
                      {request.products?.map((product, index) => (
                        <ListGroup.Item key={index} className="bg-light border-0 mb-2">
                          <div className="d-flex justify-content-between">
                            <div>
                              <div className="fw-medium">{product.name}</div>
                              <div className="text-muted small">Quantity: {product.quantity}</div>
                            </div>
                          </div>
                        </ListGroup.Item>
                      ))}
                    </ListGroup>

                    {request.notes && (
                      <div className="mt-3">
                        <h6 className="mb-2">Notes</h6>
                        <Card className="bg-light">
                          <Card.Body>
                            <p className="mb-0">{request.notes}</p>
                          </Card.Body>
                        </Card>
                      </div>
                    )}

                    <Card className="bg-light mt-3">
                      <Card.Body>
                        <div className="d-flex justify-content-between mb-2">
                          <span className="text-muted">Order Total</span>
                          <span>${request.orderTotal?.toFixed(2)}</span>
                        </div>
                        <div className="d-flex justify-content-between fw-bold">
                          <span>Courier Payment (78%)</span>
                          <span>${request.courierPayment?.toFixed(2)}</span>
                        </div>
                      </Card.Body>
                    </Card>
                  </div>
                </div>
              </div>
            )}
          </Card.Body>
        </Card>
      ))}
    </div>
  );
};

export default RecentCourierRequests;